import { useState } from "react"
import {
  Box,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Chip,
  Collapse,
  Divider,
  selectClasses,
} from "@mui/material"
import {
  Search as SearchIcon,
  Add as AddIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon,
  Visibility as VisibilityIcon,
  Edit as EditIcon,
  Comment as CommentIcon,
} from "@mui/icons-material"
import { useApiQuery, useApiMutation } from "../../hooks/useApi"
import { useQueryClient } from "@tanstack/react-query"
import { Link } from "react-router-dom"
import JobsTable from "../../components/jobs/JobsTable"
import JobDetailsDialog from "../../components/jobs/JobDetailsDialog"
import EditJobDialog from "../../components/jobs/EditJobDialog"

function JobsPage() {
  const [searchTerm, setSearchTerm] = useState("")
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false)
  const [selectedJob, setSelectedJob] = useState(null)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [viewDialogOpen, setViewDialogOpen] = useState(false)
  const [addCommentDialogOpen, setAddCommentDialogOpen] = useState(false)
  const [comment, setComment] = useState("")

  // Advanced filter states
  const [filters, setFilters] = useState({
    status: "All",
    subStatus: "All",
    assignedTo: "All",
    clientName: "",
    clientPan: "",
    jobId: "",
    jobType: "All",
  })

  // const [editData, setEditData] = useState({
  //   jobId: "",
  //   fees: "",
  //   dueDate: "",
  //   assignedTo: "",
  //   status: "",
  //   jobTypeId: "",
  // })

  const queryClient = useQueryClient()

  const { data: jobs, isLoading, refetch } = useApiQuery(["jobs"], "/jobs")
  const { data: users } = useApiQuery(["users"], "/users")
  const { data: jobList } = useApiQuery(["jobTypes"], "/job-list")

  const updateJobMutation = useApiMutation(
    `/jobs/update/${selectedJob?.JobId}`,
    "PATCH",
    {
      onSuccess: () => {
        setEditDialogOpen(false)
        refetch()
      },
    }
  )

  const setJobCommentMutation = useApiMutation(
    `/jobs/${selectedJob?.JobId}/comments`,
    "POST",
    {
      onSuccess: () => {
        setAddCommentDialogOpen(false)
        setComment("")
        queryClient.invalidateQueries({ queryKey: "jobs" })
      },
    }
  )

  const STATUSES = ["new", "processing", "completed", "cancelled", "closed"]
  const SUB_STATUSES = [
    "documentsPending",
    "taxPending",
    "verificationPending",
    "clientNotResponding",
    "keptOnHold",
  ]

  const getSubStatusLabel = (subStatus) => {
    const labels = {
      documentsPending: "Documents Pending",
      taxPending: "Tax Pending",
      verificationPending: "Verification Pending",
      clientNotResponding: "Client Not Responding",
      keptOnHold: "Kept On Hold",
    }
    return labels[subStatus] || subStatus
  }

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }))
  }

  const handleClearFilters = () => {
    setFilters({
      status: "All",
      subStatus: "All",
      assignedTo: "All",
      clientName: "",
      clientPan: "",
      jobId: "",
      jobType: "All",
    })
    setSearchTerm("")
  }

  const getActiveFilterCount = () => {
    let count = 0
    if (filters.status !== "All") count++
    if (filters.subStatus !== "All") count++
    if (filters.assignedTo !== "All") count++
    if (filters.clientName) count++
    if (filters.clientPan) count++
    if (filters.jobId) count++
    if (filters.jobType !== "All") count++
    if (searchTerm) count++
    return count
  }

  // Advanced filtering logic
  const displayJobs = jobs || []
  const filteredJobs = displayJobs?.filter((job) => {
    const matchesSearch =
      !searchTerm ||
      job.JobId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job?.clientName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job?.jobType?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (job?.assignedToName &&
        job.assignedToName.toLowerCase().includes(searchTerm.toLowerCase()))

    const matchesStatus =
      filters.status === "All" || job.status === filters.status
    const matchesSubStatus =
      filters.subStatus === "All" || job.subStatus === filters.subStatus
    const matchesAssignedTo =
      filters.assignedTo === "All" || job.assignedToId === filters.assignedTo
    const matchesClientName =
      !filters.clientName ||
      job?.clientName?.toLowerCase().includes(filters.clientName.toLowerCase())
    const matchesClientPan =
      !filters.clientPan ||
      job?.clientPan?.toLowerCase().includes(filters.clientPan.toLowerCase())
    const matchesJobId =
      !filters.jobId ||
      job?.JobId?.toLowerCase().includes(filters.jobId.toLowerCase())
    const matchesJobType =
      filters.jobType === "All" || job.jobTypeId === filters.jobType

    return (
      matchesSearch &&
      matchesStatus &&
      matchesSubStatus &&
      matchesAssignedTo &&
      matchesClientName &&
      matchesClientPan &&
      matchesJobId &&
      matchesJobType
    )
  })

  // Define table columns with custom render for assignedTo
  const columns = [
    { id: "jobId", label: "Job ID", field: "JobId", type: "jobId" },
    { id: "client", label: "Client", field: "clientName" },
    { id: "jobType", label: "Job Type", field: "jobType" },
    { id: "dueDate", label: "Due Date", field: "dueDate", type: "date" },
    { id: "status", label: "Status", field: "status", type: "status" },
    {
      id: "assignedTo",
      label: "Assigned To",
      render: (job) => (
        <Box>
          <Typography variant="body2">
            {job.assignedToName || "Unassigned"}
          </Typography>
          {job.secondaryUser && job.secondaryUser.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              + {job.secondaryUser.map((u) => u.name).join(", ")}
            </Typography>
          )}
        </Box>
      ),
    },
    { id: "createdAt", label: "Created At", field: "createdAt", type: "date" },
  ]

  // Define actions
  const actions = [
    {
      id: "view",
      label: "View Details",
      icon: <VisibilityIcon />,
    },
    {
      id: "edit",
      label: "Edit Job",
      icon: <EditIcon />,
    },
    {
      id: "addComment",
      label: "Add Comment",
      icon: <CommentIcon />,
    },
  ]

  const handleActionClick = (action, job) => {
    setSelectedJob(job)

    switch (action.id) {
      case "view":
        setViewDialogOpen(true)
        break
      case "addComment":
        setAddCommentDialogOpen(true)
        break
      case "edit":
        setEditDialogOpen(true)
        break
      default:
        break
    }
  }

  const handleEditSave = async (editData) => {
    try {
      if (!editData.JobId) {
        console.error("No job ID available for update")
        return
      }

      // console.log("handleEditSave editData", editData)

      // console.log("handleEditSave selectedJob", selectedJob)

      await updateJobMutation.mutateAsync({
        data: {
          fees: editData.fees,
          dueDate: editData.dueDate,
          assignedTo: editData.assignedTo,
          status: editData.status,
          jobTypeId: editData.jobTypeId,
          secondaryUser: editData.secondaryUsers,
        },
      })
    } catch (error) {
      console.error("Failed to update job:", error)
    }
  }

  const handleAddCommentSave = async () => {
    try {
      if (!comment) return
      const data = { commentText: comment }
      await setJobCommentMutation.mutateAsync(data)
    } catch (error) {
      console.log("Failed to add comment ", error)
    }
  }

  if (isLoading) return <Typography>Loading...</Typography>

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1">
          Jobs Management
        </Typography>
        <Button
          component={Link}
          variant="contained"
          startIcon={<AddIcon />}
          to="/add-job"
        >
          Create New Job
        </Button>
      </Box>

      {/* Search + Filter */}
      <Paper sx={{ mb: 2 }}>
        <Box sx={{ p: 2 }}>
          {/* Quick Search Bar */}
          <Box sx={{ display: "flex", gap: 2, alignItems: "center", mb: 2 }}>
            <TextField
              placeholder="Quick search by ID, client, type, or assignee..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1 }}
              size="small"
            />
            <Button
              variant={showAdvancedFilters ? "contained" : "outlined"}
              startIcon={<FilterListIcon />}
              onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
              endIcon={
                getActiveFilterCount() > 0 && (
                  <Chip
                    label={getActiveFilterCount()}
                    size="small"
                    color="primary"
                    sx={{ height: 20, ml: -0.5 }}
                  />
                )
              }
            >
              Filters
            </Button>
            {getActiveFilterCount() > 0 && (
              <Button
                variant="text"
                startIcon={<ClearIcon />}
                onClick={handleClearFilters}
                color="error"
              >
                Clear All
              </Button>
            )}
          </Box>

          {/* Advanced Filters */}
          <Collapse in={showAdvancedFilters}>
            <Divider sx={{ mb: 2 }} />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    value={filters.status}
                    onChange={(e) => handleFilterChange("status", e.target.value)}
                    label="Status"
                  >
                    <MenuItem value="All">All Status</MenuItem>
                    {STATUSES.map((status) => (
                      <MenuItem key={status} value={status}>
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Sub-Status</InputLabel>
                  <Select
                    value={filters.subStatus}
                    onChange={(e) =>
                      handleFilterChange("subStatus", e.target.value)
                    }
                    label="Sub-Status"
                  >
                    <MenuItem value="All">All Sub-Status</MenuItem>
                    {SUB_STATUSES.map((subStatus) => (
                      <MenuItem key={subStatus} value={subStatus}>
                        {getSubStatusLabel(subStatus)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Assigned To</InputLabel>
                  <Select
                    value={filters.assignedTo}
                    onChange={(e) =>
                      handleFilterChange("assignedTo", e.target.value)
                    }
                    label="Assigned To"
                  >
                    <MenuItem value="All">All Users</MenuItem>
                    {users?.map((user) => (
                      <MenuItem key={user._id} value={user._id}>
                        {user.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth size="small">
                  <InputLabel>Job Type</InputLabel>
                  <Select
                    value={filters.jobType}
                    onChange={(e) =>
                      handleFilterChange("jobType", e.target.value)
                    }
                    label="Job Type"
                  >
                    <MenuItem value="All">All Job Types</MenuItem>
                    {jobList?.map((job) => (
                      <MenuItem key={job._id} value={job._id}>
                        {job.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Job ID"
                  placeholder="Search by Job ID"
                  value={filters.jobId}
                  onChange={(e) => handleFilterChange("jobId", e.target.value)}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client Name"
                  placeholder="Search by client name"
                  value={filters.clientName}
                  onChange={(e) =>
                    handleFilterChange("clientName", e.target.value)
                  }
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  size="small"
                  label="Client PAN"
                  placeholder="Search by PAN"
                  value={filters.clientPan}
                  onChange={(e) =>
                    handleFilterChange("clientPan", e.target.value)
                  }
                />
              </Grid>
            </Grid>
          </Collapse>

          {/* Active Filters Display */}
          {getActiveFilterCount() > 0 && (
            <Box sx={{ mt: 2, display: "flex", gap: 1, flexWrap: "wrap" }}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ alignSelf: "center" }}
              >
                Active filters:
              </Typography>
              {filters.status !== "All" && (
                <Chip
                  label={`Status: ${filters.status}`}
                  size="small"
                  onDelete={() => handleFilterChange("status", "All")}
                />
              )}
              {filters.subStatus !== "All" && (
                <Chip
                  label={`Sub-Status: ${getSubStatusLabel(filters.subStatus)}`}
                  size="small"
                  onDelete={() => handleFilterChange("subStatus", "All")}
                />
              )}
              {filters.assignedTo !== "All" && (
                <Chip
                  label={`Assigned: ${
                    users?.find((u) => u._id === filters.assignedTo)?.name
                  }`}
                  size="small"
                  onDelete={() => handleFilterChange("assignedTo", "All")}
                />
              )}
              {filters.jobType !== "All" && (
                <Chip
                  label={`Type: ${
                    jobList?.find((j) => j._id === filters.jobType)?.name
                  }`}
                  size="small"
                  onDelete={() => handleFilterChange("jobType", "All")}
                />
              )}
              {filters.jobId && (
                <Chip
                  label={`Job ID: ${filters.jobId}`}
                  size="small"
                  onDelete={() => handleFilterChange("jobId", "")}
                />
              )}
              {filters.clientName && (
                <Chip
                  label={`Client: ${filters.clientName}`}
                  size="small"
                  onDelete={() => handleFilterChange("clientName", "")}
                />
              )}
              {filters.clientPan && (
                <Chip
                  label={`PAN: ${filters.clientPan}`}
                  size="small"
                  onDelete={() => handleFilterChange("clientPan", "")}
                />
              )}
              {searchTerm && (
                <Chip
                  label={`Search: ${searchTerm}`}
                  size="small"
                  onDelete={() => setSearchTerm("")}
                />
              )}
            </Box>
          )}
        </Box>
      </Paper>

      {/* Results Summary */}
      <Box
        sx={{
          mb: 2,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Showing {filteredJobs.length} of {displayJobs.length} jobs
        </Typography>
      </Box>

      {/* Jobs Table - Reusable Component */}
      <JobsTable
        jobs={filteredJobs}
        columns={columns}
        actions={actions}
        onActionClick={handleActionClick}
        emptyMessage="No jobs found matching the selected filters"
      />

      {/* View Details Dialog - Reusable Component */}
      <JobDetailsDialog
        open={viewDialogOpen}
        onClose={() => {
          setViewDialogOpen(false)
          setSelectedJob(null)
        }}
        job={selectedJob}
      />

      {/* Edit Job Dialog */}
      {/* <Dialog
        open={editDialogOpen}
        onClose={() => setEditDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Job Details</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
            <TextField
              label="Fees"
              type="number"
              value={editData.fees}
              onChange={(e) => handleEditChange("fees", e.target.value)}
              fullWidth
            />
            <TextField
              label="Due Date"
              type="date"
              value={editData.dueDate}
              onChange={(e) => handleEditChange("dueDate", e.target.value)}
              fullWidth
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: new Date().toISOString().split("T")[0] }}
            />
            <FormControl fullWidth>
              <InputLabel>Job Type</InputLabel>
              <Select
                value={editData.jobTypeId}
                onChange={(e) => handleEditChange("jobTypeId", e.target.value)}
                label="Job Type"
              >
                {jobList?.map((job) => (
                  <MenuItem key={job._id} value={job._id}>
                    {job.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Assigned To</InputLabel>
              <Select
                value={editData.assignedTo}
                onChange={(e) => handleEditChange("assignedTo", e.target.value)}
                label="Assigned To"
              >
                {users?.map((user) => (
                  <MenuItem key={user._id} value={user._id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleEditSave} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog> */}
<EditJobDialog
  open={editDialogOpen}
  onClose={() => setEditDialogOpen(false)}
  job={selectedJob}
  users={users}
  jobTypes={jobList}
  onSave={handleEditSave}
/>
      {/* Add Comment Dialog */}
      <Dialog
        open={addCommentDialogOpen}
        onClose={() => setAddCommentDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add Comment</DialogTitle>
        <DialogContent>
          {selectedJob && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="h6" gutterBottom>
                {selectedJob.JobId} - {selectedJob.jobType}
              </Typography>
              <Typography variant="body2" color="textSecondary" paragraph>
                Client: {selectedJob.clientName}
              </Typography>
              <TextField
                fullWidth
                label="Comments"
                multiline
                rows={3}
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Add notes about the update..."
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddCommentDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddCommentSave} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default JobsPage